import type { ButtonProps, LinkProps } from '@shopify/polaris';
import type { GTooltipProps } from '../GTooltip';

import { Button, Box } from '@shopify/polaris';
import { GTooltip } from '../GTooltip';
import { cls } from '~/utils';

export interface GButtonProps extends Omit<ButtonProps, 'variant'> {
  linkAction?: LinkProps;
  activatorWrapper?: 'div' | 'span';
  iconRight?: boolean;
  stopPropagation?: boolean;
  tooltip?: GTooltipProps;
  themeTone?: 'dark' | 'light';
  /**
   * Adds padding="100" wrapper to compensate for tertiary variant's -4px margin.
   * Also applies display="flex" automatically.
   * Use this when placing tertiary buttons in tight layouts.
   */
  inset?: boolean;
  variant?: ButtonProps['variant'] | 'neutral';
}

export const GButton: React.FC<GButtonProps> = ({
  activatorWrapper,
  children,
  iconRight,
  inset,
  stopPropagation = true,
  onClick,
  themeTone = 'light',
  variant,
  ...props
}) => {
  const Wrapper = activatorWrapper || 'div';
  const isNeutral = variant === 'neutral';
  const polarisVariant = isNeutral ? undefined : variant;

  const handleClick = (e: React.MouseEvent) => {
    stopPropagation && e.stopPropagation();
    if (props.disabled) return;
    onClick?.();
  };

  const button = (
    <GTooltip activatorWrapper="tooltip-content" {...props.tooltip}>
      <Wrapper
        className={cls('Polaris-GButton', {
          'Polaris-GButton--icon-right': iconRight,
          'Polaris-GButton--dark': themeTone === 'dark',
          'Polaris-GButton--neutral': isNeutral,
          'cursor-default': props.disabled,
          'Polaris-GButton--fullWidth': props.fullWidth,
          flex: inset,
        })}
        onClick={handleClick}
      >
        <Button variant={polarisVariant} {...props}>
          {children}
        </Button>
      </Wrapper>
    </GTooltip>
  );

  if (inset) {
    return <Box padding="100">{button}</Box>;
  }

  return button;
};
